import { Injectable } from '@angular/core';

import { StatsigService } from '@services/statsig.service';
import { IServiceWorkerConfig } from '@app/service-worker/bulk/bulk.sw.interface';
import { isMaintenancePage } from '@lib/url';
import { BULK_SW_CHANNEL } from '@app/service-worker/bulk/bulk.sw.consts';

// dynamic config from statsig
export const BULK_CONFIG_NAME = 'bulk-api-cache-config-enabled';
export const BULK_API_CACHE_ENABLED = 'bulk-api-cache-enabled';

export enum BulkRequests {
  DRIVER = '/api/w2/users/bulk?show_all=false&role=driver',
  VEHICLE = '/api/w2/vehicles/bulk',
  ASSET = '/api/w2/assets/bulk',
}

@Injectable({ providedIn: 'root' })
export class ServiceWorkerDynamicConfigService {
  private swBulkConfig: IServiceWorkerConfig = null;

  constructor(private statsigService: StatsigService) {
    if ('BroadcastChannel' in window) {
      const BulkSWChannel: BroadcastChannel = new BroadcastChannel(BULK_SW_CHANNEL);

      BulkSWChannel.onmessage = event => {
        this.swBulkConfig = event.data;
      };
    }
  }

  public get getConfig(): IServiceWorkerConfig {
    return this.statsigService.evaluateDynamicConfig(BULK_CONFIG_NAME).value as IServiceWorkerConfig;
  }

  private get isBulkAPICacheEnabled(): boolean {
    return !!(this.getConfig && Object.keys(this.getConfig.CACHE_API_LIST || {}).length > 0);
  }

  private isBulkAPICacheEnabledForMaintenance(entity: string) {
    return (
      this[`is${entity}BulkAPICacheEnabled`] &&
      (this.isBulkAPICacheEnabledForAllPods || isMaintenancePage()) &&
      this.isBulkServiceWorkerActiveAndValueConfigured
    );
  }

  public get isDriverBulkAPICacheEnabledForMaintenance(): boolean {
    return this.isBulkAPICacheEnabledForMaintenance('Driver');
  }

  public get isVehicleBulkAPICacheEnabledForMaintenance(): boolean {
    return this.isBulkAPICacheEnabledForMaintenance('Vehicle');
  }

  public get isAssetBulkAPICacheEnabledForMaintenance(): boolean {
    return this.isBulkAPICacheEnabledForMaintenance('Asset');
  }

    // TODO: handle this with regex based config
  public get isDriverBulkAPICacheEnabled(): boolean {
    return this.checkConfigKeys(BulkRequests.DRIVER);
  }

  public get isVehicleBulkAPICacheEnabled(): boolean {
    return this.checkConfigKeys(BulkRequests.VEHICLE);
  }

  public get isAssetBulkAPICacheEnabled(): boolean {
    return this.checkConfigKeys(BulkRequests.ASSET);
  }

  public get isBulkServiceWorkerActive(): boolean {
    if ('serviceWorker' in navigator) {
      return navigator.serviceWorker.controller?.scriptURL.includes('app.sw.js');
    }
  }

  public get isBulkAPICacheEnabledForAllPods(): boolean {
    return this.statsigService.evaluateGate(BULK_API_CACHE_ENABLED);
  }

  private checkConfigKeys(api: string): boolean {
    return (
      this.isBulkAPICacheEnabled &&
      !!Object.keys(this.getConfig.CACHE_API_LIST).find(key => new RegExp(this.getConfig.CACHE_API_LIST[key]).test(api),
      ));
  }

  private get isServiceWorkerConfigValueConfigured(): boolean {
    return !!this.swBulkConfig && Object.keys(this.swBulkConfig.CACHE_API_LIST || {}).length > 0;
  }

  private get isBulkServiceWorkerActiveAndValueConfigured(): boolean {
    return this.isBulkServiceWorkerActive && this.isServiceWorkerConfigValueConfigured;
  }
}
